import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import ColorPicker from '@/components/ColorPicker';
import { FormErrorMessage } from '@/components/FormErrorMessage';
import Tag from '@/components/Tag/Tag';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { addNewTagFrag, editTagFrag } from '@/hooks/mutations/tagMutations';
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import { useUserRole } from '@/hooks/useAuth';
import type { DisciplineWithCourses } from '@/lib/types';
import { StyledTextArea } from '@/Shared/StyledElements';

interface TagListModalProps {
	triggerModal(): void;
	modalActive: boolean;
	isEditing?: boolean;
	tagData: CourseTag;
	tagList: TagTableItem[];
	disciplines: DisciplineWithCourses[] | undefined;
}

interface FormValues {
	tagFormName: string;
	tagFormColor: string;
	tagFormDefinition: string;
}

const TagListModal = ({
	modalActive,
	triggerModal,
	isEditing,
	tagData,
	tagList,
	disciplines,
}: TagListModalProps) => {
	const {
		tag_color: tagColor,
		tag_definition: tagDefinition,
		tag_id: tagId,
		tag_name: tagName,
		tag_discipline: tagDiscipline,
	} = tagData;

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		watch,
		reset,
		control,
	} = useForm<FormValues>();
	const [selectedColor, setSelectedColor] = useState(tagColor);
	const [tagDivisions, setTagDivisions] = useState(
		isEditing ? (tagDiscipline?.split(',') ?? []) : []
	);
	const { data: userRoleData } = useUserRole();
	const queryClient = useQueryClient();
	const isMobile = useIsBreakpoint('max', 768);

	const { mutateAsync: newTagMutation } = useMutation({
		mutationFn: addNewTagFrag,
		onSuccess: () => {
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			queryClient.invalidateQueries({
				queryKey: ['get-all-tags', schoolId],
			});
			showSuccessToast('New Tag Added');
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const { mutateAsync: existingTagMutation } = useMutation({
		mutationFn: editTagFrag,
		onSuccess: () => {
			const schoolId = import.meta.env.VITE_SCHOOL_ID;
			queryClient.invalidateQueries({
				queryKey: ['get-all-tags', schoolId],
			});
			showSuccessToast('Tag Updated');
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const createTagPreviewName = () => {
		if (watch('tagFormName') === '' || watch('tagFormName')?.trim() === '') {
			return 'Tag Name';
		}
		if (isEditing) {
			return watch('tagFormName', tagName);
		}

		return watch('tagFormName', 'Tag Name');
	};

	const onSubmit: SubmitHandler<FormValues> = async (formData) => {
		const { tagFormName, tagFormColor, tagFormDefinition } = formData;

		const tagData = {
			color: tagFormColor,
			name: tagFormName.trim(),
			definition: tagFormDefinition.trim(),
			role: userRoleData?.user_role,
			discipline: tagDivisions.toString(),
		};

		if (isEditing) {
			await existingTagMutation({
				id: tagId,
				...tagData,
			} as any);
		} else {
			await newTagMutation(tagData as any);
			reset();
		}
	};

	const isUnique = (value: string) => {
		const isPresent = tagList.some(
			(tag: TagTableRow) => tag.nameColumn.toLowerCase() === value.toLowerCase()
		);
		return isPresent;
	};

	const createPreviewTag = useCallback(() => {
		let color = selectedColor;

		if (isEditing) {
			color = selectedColor || tagColor;
		}
		return color;
	}, [selectedColor, isEditing, tagColor]);

	const createDefaultValue = useCallback(() => (isEditing ? tagName : null), [tagName, isEditing]);

	function handleDisciplineChange(discipline: DisciplineWithCourses) {
		setTagDivisions((prevState: any) => {
			if (!prevState) {
				return [discipline.name];
			}

			const isExistingCourse = prevState.includes(discipline?.name ?? '');
			if (isExistingCourse) {
				return prevState.filter((d: string) => d !== discipline.name);
			}

			return [...prevState, discipline.name];
		});
	}

	const title = isEditing ? `Editing Tag: ${tagName}` : 'Adding New Tag';

	const handleOpenChange = (open: boolean) => {
		if (!open) triggerModal();
	};

	const formContent = (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<div>
				<label htmlFor="tagFormName" className="text-sm">
					Tag Name
				</label>
				<Input
					type="text"
					{...register('tagFormName', {
						required: 'Tag Name is required',
						validate: (value) => {
							if (value.trim() === '') {
								return 'Tag Name is required';
							}
							if (isEditing) {
								return true;
							}
							if (isUnique(value.trim())) {
								return 'This name already exists.';
							}
							return true;
						},
					})}
					defaultValue={createDefaultValue() ?? ''}
				/>
				<FormErrorMessage
					className="error-message"
					isShowing={!!errors?.tagFormName}
					message={errors.tagFormName?.message}
				/>
			</div>
			<div>
				{/* <StyledHR /> */}
				{/* <hr className="border-t border-border" /> */}
				<p className="text-sm pb-2">Tag Preview</p>
				<div className="flex items-center gap-8">
					<Tag text={createTagPreviewName()} color={selectedColor} />
					<Controller
						control={control}
						name="tagFormColor"
						defaultValue={tagColor}
						render={({ field }) => {
							return (
								<ColorPicker
									onChange={(color) => {
										setSelectedColor(field.value);
										field.onChange(color.hex);
									}}
									color={createPreviewTag()}
								/>
							);
						}}
					/>
				</div>
			</div>
			<hr className="border-0 shadow-[0px_2px_0px_-1px_rgb(50_50_50/10%)] pb-4 w-full" />
			<div>
				<label htmlFor="tagFormDefinition" className="text-sm">
					Tag Definition
				</label>
				<StyledTextArea
					className="textarea"
					defaultValue={isEditing ? tagDefinition : ''}
					{...register('tagFormDefinition', {
						required: 'Tag Definition is required',
						validate: (value) => {
							if (value.trim() === '') {
								return 'Tag Definition is required';
							}
							return true;
						},
					})}
				/>
				<FormErrorMessage
					className="error-message"
					isShowing={!!errors?.tagFormDefinition}
					message={errors.tagFormDefinition?.message}
				/>
			</div>
			<div className="flex flex-wrap w-full">
				<p className="w-full text-sm mt-4 pb-1">Tag Disciplines</p>
				<p className="w-full text-sm pb-4">
					If no discipline is selected, then this tag will be available to all disciplines.
				</p>
				{disciplines?.map((d) => {
					const { id, name } = d;
					// check for exact discipline name
					const regex = new RegExp(`\\b(?:${name})\\b`, 'gi');
					// set checked to true if discipline is existing when editing or default to false
					const isChecked = isEditing ? Boolean(tagDiscipline?.match(regex)) : false;
					return (
						<label
							key={id}
							htmlFor={name ?? ''}
							className="flex items-center cursor-pointer w-1/2 mb-4 font-size-unset"
						>
							<Checkbox
								id={name ?? ''}
								defaultChecked={isChecked}
								onCheckedChange={() => handleDisciplineChange(d)}
								className="mr-2"
							/>
							{name}
						</label>
					);
				})}
			</div>
			<hr className="border-0 shadow-[0px_2px_0px_-1px_rgb(50_50_50/10%)] mb-6 pb-4 w-full" />
		</form>
	);

	const saveButton = (
		<Button
			isLoading={isSubmitting}
			disabled={isSubmitting}
			type="submit"
			onClick={handleSubmit(onSubmit)}
		>
			Save Tag
		</Button>
	);

	const description = isEditing
		? "Update this tag's name, color, definition, and disciplines."
		: 'Add a name, color, definition, and disciplines for the new tag.';

	if (isMobile) {
		return (
			<Drawer open={modalActive} onOpenChange={handleOpenChange}>
				<DrawerContent className="max-h-[85vh]">
					<DrawerHeader>
						<DrawerTitle>{title}</DrawerTitle>
						<DrawerDescription className="text-base">{description}</DrawerDescription>
					</DrawerHeader>
					<div className="px-4 pb-2 flex-1 overflow-y-auto">{formContent}</div>
					<DrawerFooter>{saveButton}</DrawerFooter>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog open={modalActive} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-[800px]">
				<DialogHeader>
					<DialogTitle className="mb-2">{title}</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>
				<div className="max-h-[70vh] overflow-y-auto pl-1">{formContent}</div>
				<div className="flex justify-end">{saveButton}</div>
			</DialogContent>
		</Dialog>
	);
};

export default TagListModal;
